<template>
  <vs-popup ref="employeePopup" id="popupSearchEmployee" :title="$t('종사자 검색')" :active.sync="showPopup">
    <div class="popupSearchEmployee_list">
      <!-- 모바일 레이아웃 -->
      <div v-if="isMobile" class="mobile-search-form">
        <!-- 필터 토글 헤더 -->
        <div class="mobile-filter-header" @click="toggleMobileFilter">
          <span>{{ $t('검색 필터') }}</span>
          <i class="material-icons mobile-filter-icon" :class="{ 'rotated': isMobileFilterExpanded }">expand_more</i>
        </div>

        <!-- 접을 수 있는 필터 영역 -->
        <div class="mobile-filter-content" :class="{ 'expanded': isMobileFilterExpanded }">
          <div class="mobile-form-group">
            <label>{{ $t('사업장명') }}</label>
            <field-selector v-model="selectedFieldId" @select="handleFieldSelect" />
          </div>
          <div class="mobile-form-group">
            <label>{{ $t('부서명') }}</label>
            <company-selector v-model="selectedCompanyId" @click.stop @select="handlerCompanySelect" />
          </div>
          <div class="mobile-form-group">
            <label>{{ $t('검색조건') }}</label>
            <vs-select v-model="filterType" :placeholder="$t('전체')" autocomplete>
              <vs-select-item v-for="selectData in filterTypeOptions" :key="selectData.value" :text="$t(selectData.label)" :value="selectData.value" />
            </vs-select>
          </div>
          <div class="mobile-form-group">
            <label>{{ $t('검색어') }}</label>
            <div class="mobile-search-input">
              <search-nav-item-keyword :view-label="false" :placeholder="$t('검색')" v-model="search.keyword" @keyup-event="handleClickSearch" />
              <vs-button color="secondary" @click="handleClickSearch" class="mobile-search-btn">{{ $t('검색') }}</vs-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 데스크톱 레이아웃 -->
      <div v-if="!isMobile">
        <vs-row class="text-center p-4 grid grid-cols-12">
          <vs-col class="grid grid-cols-10 col-span-10 gap-3" vs-align="center">
            <vs-col class="grid grid-cols-3 col-span-3" vs-align="center">
              <span class="col-span-1 text-left">{{ $t('사업장명') }}</span>
              <field-selector class="col-span-2" v-model="selectedFieldId" @select="handleFieldSelect" />
            </vs-col>
            <vs-col class="grid grid-cols-3 col-span-3" vs-align="center">
              <span class="col-span-1 text-left">{{ $t('부서명') }}</span>
              <company-selector class="col-span-2" v-model="selectedCompanyId" @click.stop @select="handlerCompanySelect" />
            </vs-col>
          </vs-col>
        </vs-row>

        <!-- 검색조건, 키워드 입력 폼을 추가한다 -->
        <vs-row class="px-4 pb-4 grid grid-cols-12" style="border-bottom: 1px solid #e0e0e0">
          <vs-col class="grid grid-cols-10 col-span-10 gap-3" vs-align="center">
            <vs-col class="grid grid-cols-3 col-span-3" vs-align="center">
              <span class="col-span-1 text-left">{{ $t('검색조건') }}</span>
              <vs-select class="col-span-2 w-full" v-model="filterType" :placeholder="$t('전체')" autocomplete>
                <vs-select-item v-for="selectData in filterTypeOptions" :key="selectData.value" :text="$t(selectData.label)" :value="selectData.value" />
              </vs-select>
            </vs-col>
            <vs-col class="grid grid-cols-3 col-span-3" vs-align="center">
              <search-nav-item-keyword class="col-span-3" :view-label="false" :placeholder="$t('검색')" v-model="search.keyword" @keyup-event="handleClickSearch" />
            </vs-col>
          </vs-col>
          <vs-col class="col-span-2 text-right">
            <vs-button color="secondary" @click="handleClickSearch">{{ $t('검색') }}</vs-button>
          </vs-col>
        </vs-row>
      </div>

      <!-- 모바일 카드 레이아웃 -->
      <div v-if="isMobile" class="mobile-content">
        <!-- 전체 선택 헤더 -->
        <div class="mobile-select-all">
          <vs-checkbox :value="allCheck" @click.native="onCheckAll" />
          <span>{{ $t('전체 선택') }}</span>
          <span class="selected-count">({{ checkEmployeeIds.length }}{{ $t('명 선택') }})</span>
        </div>

        <!-- 직원 카드 리스트 -->
        <div v-if="employeeList.length > 0" class="mobile-card-list">
          <div v-for="(employee, index) in employeeList" :key="employee.id"
               class="mobile-employee-card"
               :class="{ 'selected': checkEmployeeIds.includes(employee.id) }">
            <div class="card-header" @click="toggleEmployeeSelection(employee)">
              <vs-checkbox
                :vs-value="employee.id"
                v-model="checkEmployeeIds"
                @change="() => onCheckEmployee(employee)"
                class="card-checkbox"
                @click.stop
              />
              <div class="employee-main-info">
                <h3 class="employee-name">{{ employee.full_name }}</h3>
                <span class="employee-id">{{ employee.employee_id || 'ID 없음' }}</span>
              </div>
            </div>

            <div class="card-body">
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">{{ $t('부서명') }}</span>
                  <span class="info-value">{{ employee.company.com_name }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('구분') }}</span>
                  <span class="info-value">{{ employee.job_category }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('직종') }}</span>
                  <span class="info-value">{{ employee.employment_type }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('직급') }}</span>
                  <span class="info-value">{{ employee.job_title }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('연락처') }}</span>
                  <span class="info-value">{{ employee.contact_number }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('입사일') }}</span>
                  <span class="info-value">{{ employee.hire_date }}</span>
                </div>
                <div class="info-item" v-if="employee.current_position_date">
                  <span class="info-label">{{ $t('현보직일') }}</span>
                  <span class="info-value">{{ employee.current_position_date }}</span>
                </div>
                <div class="info-item" v-if="employee.retirement_date">
                  <span class="info-label">{{ $t('퇴임일') }}</span>
                  <span class="info-value">{{ employee.retirement_date }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 빈 목록 안내 -->
        <div v-else class="mobile-empty-state">
          <p>{{ $t('데이터가 없습니다') }}</p>
        </div>
      </div>

      <!-- 데스크톱 테이블 레이아웃 -->
      <vs-row v-if="!isMobile" class="p-4">
        <vs-col>
          <vs-table stripe ref="table" :sst="true" :max-items="page.perPage" :data="employeeList" :noDataText="$t('데이터가 없습니다')">
            <template slot="thead">
              <vs-th style="width: 40px"><vs-checkbox :value="allCheck" @click.native="onCheckAll" /></vs-th>
              <vs-th style="width: 60px"><span class="w-full text-center">No.</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('부서명') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('ID(사번)') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('구분') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('직종') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('직급') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('성명') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('최종 입사일') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('현보직일') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('연락처') }}</span></vs-th>
              <vs-th style="width: 9%"><span class="w-full text-center">{{ $t('교육이수 횟수') }}</span></vs-th>
              <vs-th><span class="w-full text-center">{{ $t('퇴임일') }}</span></vs-th>
            </template>

            <template slot-scope="{ data }">
              <vs-tr :data="employee" :key="index" v-for="(employee, index) in data">
                <vs-td class="text-center"><vs-checkbox :vs-value="employee.id" v-model="checkEmployeeIds" @change="() => onCheckEmployee(employee)" /></vs-td>
                <vs-td class="text-center">{{ employee.id }}</vs-td>
                <vs-td class="text-center">{{ /*부서명*/ employee.company.com_name }}</vs-td>
                <vs-td class="text-center">{{ /*사번*/ employee.employee_id }}</vs-td>
                <vs-td class="text-center">{{ /*구분*/ employee.job_category }}</vs-td>
                <vs-td class="text-center">{{ /*직종*/ employee.employment_type }}</vs-td>
                <vs-td class="text-center">{{ /*직급*/ employee.job_title }}</vs-td>
                <vs-td class="text-center">{{ /*성명*/ employee.full_name }}</vs-td>
                <vs-td class="text-center">{{ /*최종 입사일*/ employee.hire_date }}</vs-td>
                <vs-td class="text-center">{{ /*현보직일*/ employee.current_position_date }}</vs-td>
                <vs-td class="text-center">{{ /*연락처*/ employee.contact_number }}</vs-td>
                <vs-td class="text-center">{{ /*교육이수 횟수*/ 0 }}</vs-td>
                <vs-td class="text-center">{{ /*퇴임일*/ employee.retirement_date }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </vs-col>
      </vs-row>
      <!-- 모바일 하단 고정 영역 -->
      <div v-if="isMobile" class="mobile-bottom-area">
        <div class="mobile-pagination">
          <k-pagination :total="page.lastPage ? page.lastPage : 0" v-model="page.current" @change="handleChangePage"></k-pagination>
        </div>
        <div class="mobile-action-buttons">
          <vs-button class="mobile-cancel-btn" color="#EDEDED" text-color="black" type="filled" @click="showPopup = false">
            {{ $t('닫기') }}
          </vs-button>
          <vs-button class="mobile-select-btn" color="secondary" @click="handleClickSelect">
            {{ $t('선택') }} ({{ checkEmployeeIds.length }})
          </vs-button>
        </div>
      </div>

      <!-- 데스크톱 하단 영역 -->
      <vs-row v-if="!isMobile">
        <vs-col>
          <div class="vs-con-table">
            <div class="con-pagination-table vs-table--pagination">
              <k-pagination :total="page.lastPage ? page.lastPage : 0" v-model="page.current" @change="handleChangePage"></k-pagination>
            </div>
          </div>
        </vs-col>
        <vs-col class="mt-4 flex p-4" vs-align="center" vs-justify="flex-end">
          <vs-button class="mr-4" color="#EDEDED" text-color="black" type="filled" @click="showPopup = false">{{ $t('닫기') }}</vs-button>
          <vs-button class="" color="secondary" @click="handleClickSelect">{{ $t('선택') }}</vs-button>
        </vs-col>
      </vs-row>
    </div>
  </vs-popup>
</template>

<script>
import KPagination from "@/components/kPagination/kPagination";
import FileUploader from "@/components/FileUploader.vue";
import AddressField from "@/components/AddressField.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import CompanySelector from "@/components/selector/CompanySelector.vue";
import SearchNavItemKeyword from "@/components/nav/SearchNavItemKeyword.vue";
export default {
  name: "PopupSearchEmployeeCheckbox",
  components: {
    SearchNavItemKeyword,
    CompanySelector,
    FieldSelector,
    AddressField,
    FileUploader,
    KPagination,
  },
  props: {
    value: Boolean,
    // 팝업이 열릴 때, 선택된 ID가 있을 경우 해당 ID를 선택한 상태로 팝업을 열기 위한 값
    initSelectedEmployees: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    projectList() {
      return this.$store.state.project.projectListInfo.data;
    },
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    employeeList() {
      if (!this.apiResponseData) return [];

      if (this.apiResponseData.data) {
        return this.apiResponseData.data;
      } else {
        return [];
      }
    },
    allCheck() {
      return this.employeeList.reduce((acc, cur) => {
        return acc && this.checkEmployeeIds.includes(cur.id);
      }, true);
    },
  },
  data() {
    return {
      isMobile: false,
      windowWidth: 0,
      popupWidth: 330,
      showPopup: this.value,
      originalScrollPosition: undefined,

      checkEmployee: [],
      checkEmployeeIds: [],

      selectedField: {},
      selectedFieldId: 0,
      selectedCompany: {},
      selectedCompanyId: 0,
      filterType: "",
      filterTypeOptions: [
        {
          label: "전체",
          value: "",
        },
        {
          label: "ID(사번)",
          value: "employee_id",
        },
        {
          label: "부서",
          value: "com_name",
        },
        {
          label: "구분",
          value: "job_category",
        },
        {
          label: "직종",
          value: "employment_type",
        },
        {
          label: "직급",
          value: "job_title",
        },
        {
          label: "연락처(마지막 4자리)",
          value: "contact_number",
        },
      ],

      // 현재 검색되어 반환된 리스트
      apiResponseData: [],

      // 페이징
      page: {
        current: 1,
        lastPage: 1,
        perPage: 5,
      },

      // 겅색
      search: {
        keyword: "", // 검색어
      },

      // 모바일 필터 확장 상태
      isMobileFilterExpanded: false,
    };
  },
  watch: {
    value(data) {
      this.showPopup = data;
      if (data) {
        this.loadEmployeeList();

        this.checkEmployee = this.initSelectedEmployees;
        this.checkEmployeeIds = this.initSelectedEmployees.map((item) => item.id);
      }
    },
    initSelectedEmployees(data) {
      this.checkEmployee = data;
      this.checkEmployeeIds = data.map((item) => item.id);
    },
    showPopup(data) {
      this.$emit("input", data);

      if (data) {
        // 백그라운드 스크롤 방지
        this.preventBackgroundScroll();

        // popup이 렌더링될 시간을 주기 위해 nextTick 사용
        this.$nextTick(() => {
          this.popupWidth = this.getPopupWidth();
        });
      } else {
        // 백그라운드 스크롤 복원
        this.restoreBackgroundScroll();
      }
    },
  },
  created() {
    this.updateWindowSize();
    window.addEventListener('resize', this.updateWindowSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowSize);
    // 컴포넌트 제거 시 백그라운드 스크롤 복원
    this.restoreBackgroundScroll();
  },
  mounted() {
    const backButtonRouteGuard = this.$router.beforeEach((to, from, next) => {
      if (this.showPopup) {
        this.showPopup = false;
        next(false);
        return;
      }
      next(true);
    });
    this.$once("hook:destroyed", () => {
      backButtonRouteGuard();
    });
  },
  methods: {
    onCheckAll() {
      if (this.allCheck) {
        // 전체해제
        const empListIds = this.employeeList.map((item) => item.id);
        this.checkEmployee = this.checkEmployee.filter((item) => !empListIds.includes(item.id));
      } else {
        // 전체체크
        for (const data of this.employeeList) {
          if (!this.checkEmployeeIds.includes(data.id)) this.checkEmployee.push(data);
        }
      }

      this.checkEmployeeIds = this.checkEmployee.map((item) => item.id);
    },
    onCheckEmployee(data) {
      if (this.checkEmployeeIds.includes(data.id)) this.checkEmployee.push(data);
      else this.checkEmployee = this.checkEmployee.filter((item) => item.id !== data.id);
    },

    // 카드 헤더 클릭 시 직원 선택 토글
    toggleEmployeeSelection(employee) {
      if (this.checkEmployeeIds.includes(employee.id)) {
        // 선택 해제
        this.checkEmployee = this.checkEmployee.filter((item) => item.id !== employee.id);
        this.checkEmployeeIds = this.checkEmployee.map((item) => item.id);
      } else {
        // 선택 추가
        this.checkEmployee.push(employee);
        this.checkEmployeeIds = this.checkEmployee.map((item) => item.id);
      }
    },
    async loadEmployeeList() {
      const page = this.page.current;
      const perPage = this.page.perPage;
      const projectId = this.projectId;

      const payload = {
        projectId,
        page,
        perPage,
      };
      if (this.selectedFieldId > 0) {
        payload.field_ids = [this.selectedFieldId];
      }
      if (this.selectedCompanyId > 0) {
        payload.company_id = this.selectedCompanyId;
      }

      // 선택한 필터링 타입에 맞게 검색어를 전달한다
      switch (this.filterType) {
        case "":
          payload.keyword = this.search.keyword;
          break;
        case "employee_id":
          payload.employee_id = this.search.keyword;
          break;
        case "com_name":
          payload.com_name = this.search.keyword;
          break;
        case "job_category":
          payload.job_category = this.search.keyword;
          break;
        case "employment_type":
          payload.employment_type = this.search.keyword;
          break;
        case "job_title":
          payload.job_title = this.search.keyword;
          break;
        case "contact_number":
          payload.contact_number = this.search.keyword;
          break;
      }

      this.apiResponseData = JSON.parse(JSON.stringify(await this.$store.dispatch("employee/LOAD_EMPLOYEE_LIST", payload)));

      // employee_id가 없는 경우가 undefined 로 넘어오기 때문에 ''로 변경
      if (this.apiResponseData && this.apiResponseData.data && this.apiResponseData.data.length > 0) {
        this.apiResponseData.data.forEach((item) => {
          if (item.employee_id === 'undefined') {
            item.employee_id = '';
          }
        });
      }

      this.page.current = this.apiResponseData.current_page;
      this.page.perPage = this.apiResponseData.per_page;
      this.page.lastPage = this.apiResponseData.last_page;
    },

    handleClickSearch() {
      this.loadEmployeeList();
    },

    handleChangePage(page) {
      this.page.current = page;
      this.loadEmployeeList();
    },

    handleClickSelect() {
      this.$emit("input", false);
      this.$emit("handleClickSelect", this.checkEmployee);
    },

    // 사업장이 선택됐을 대
    handleFieldSelect(fieldData) {
      this.selectedField = fieldData;
      this.loadEmployeeList();
    },
    // 부서가 선택됐을 때
    handlerCompanySelect(companyData) {
      this.selectedCompany = companyData;
      this.selectedCompanyId = companyData.id;
      this.loadEmployeeList();
    },

    updateWindowSize() {
      this.windowWidth = window.innerWidth;
      this.isMobile = this.windowWidth < 768;
    },

    // 모바일 필터 토글
    toggleMobileFilter() {
      this.isMobileFilterExpanded = !this.isMobileFilterExpanded;
    },

    getPopupWidth() {
      if (this.$refs.employeePopup) {
        // popup 요소의 실제 너비 가져오기
        const popupElement = this.$refs.employeePopup.$el.querySelector('.vs-popup');
        if (popupElement) {
          const width = popupElement.offsetWidth;
          return width;
        }
      }
      return 0;
    },

    // 백그라운드 스크롤 방지
    preventBackgroundScroll() {
      if (this.isMobile) {
        // 현재 스크롤 위치 저장
        this.originalScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

        // body 고정
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.originalScrollPosition}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
      }
    },

    // 백그라운드 스크롤 복원
    restoreBackgroundScroll() {
      if (this.isMobile) {
        // body 스타일 복원
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';

        // 원래 스크롤 위치로 복원
        if (this.originalScrollPosition !== undefined) {
          window.scrollTo(0, this.originalScrollPosition);
          this.originalScrollPosition = undefined;
        }
      }
    }
  },
};
</script>

<style>
#popupSearchEmployee {
  z-index: 52000 !important;
}

/* 데스크톱 팝업 스타일 */
#popupSearchEmployee > .vs-popup {
  width: 1600px !important;
  max-width: 80% !important;
  max-height: 80% !important;
}

/* 모바일 팝업 스타일 */
@media (max-width: 767px) {
  #popupSearchEmployee > .vs-popup {
    width: 100vw !important;
    height: 100vh !important;
    max-width: none !important;
    max-height: none !important;
    margin: 0 !important;
    border-radius: 0 !important;
  }

  /* 모바일에서 팝업 헤더의 닫기 버튼 숨기기 */
  #popupSearchEmployee .vs-popup--close {
    display: none !important;
  }
}

#popupSearchEmployee > .vs-popup > .vs-popup--content {
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
}

.popupSearchEmployee_new {
  height: calc(100% - 40px);
  display: grid;
  grid-auto-rows: auto 1fr auto;
}

.popupSearchEmployee_list {
  height: calc(100% - 40px);
  display: grid;
  grid-auto-rows: auto auto 1fr auto;
}

/* 모바일 레이아웃에서 고정 하단 영역을 위한 조정 */
@media (max-width: 767px) {
  .popupSearchEmployee_list {
    height: 100vh;
    grid-template-rows: auto 1fr;
    padding-bottom: 0; /* 하단 고정 영역을 별도 처리하므로 여백 제거 */
  }
}

/* 모바일 검색 폼 스타일 */
.mobile-search-form {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

/* 모바일 필터 헤더 */
.mobile-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #6c5ce7;
  color: white;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
}

.mobile-filter-icon {
  transition: transform 0.3s ease;
}

.mobile-filter-icon.rotated {
  transform: rotate(180deg);
}

/* 모바일 필터 콘텐츠 */
.mobile-filter-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: 0 16px;
}

.mobile-filter-content.expanded {
  max-height: 500px;
  padding: 16px;
}

.mobile-form-group {
  margin-bottom: 12px;
}

.mobile-form-group:last-child {
  margin-bottom: 0;
}

.mobile-form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 6px;
}

.mobile-search-input {
  display: flex;
  gap: 8px;
}

.mobile-search-input > * {
  flex: 1;
}

.mobile-search-btn {
  flex: 0 0 auto !important;
  min-height: 44px;
  padding: 0 16px;
}

/* 모바일 콘텐츠 영역 */
.mobile-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px 16px;
  min-height: 0; /* flex 아이템이 올바르게 축소되도록 */
  margin-bottom: 60px; /* 하단 고정 영역 높이만큼 여백 */
  background-color: #e9ecef;
}

/* 모바일 전체 선택 헤더 */
.mobile-select-all {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
  margin: 0 -16px 16px -16px;
  padding-left: 16px;
  padding-right: 16px;
}

.selected-count {
  color: #6c757d;
  font-size: 14px;
  margin-left: auto;
}

/* 모바일 카드 리스트 */
.mobile-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-employee-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.mobile-employee-card.selected {
  border: 2px solid #6c5ce7;
  box-shadow: 0 4px 12px rgba(108, 92, 231, 0.15);
  transform: translateY(-1px);
}

.mobile-employee-card:last-child {
  margin-bottom: 60px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;
}

.card-header:hover {
  background: #e9ecef;
}

.mobile-employee-card.selected .card-header {
  background: rgba(108, 92, 231, 0.1);
  border-bottom-color: rgba(108, 92, 231, 0.2);
}

.mobile-employee-card.selected .card-header:hover {
  background: rgba(108, 92, 231, 0.15);
}

.card-checkbox {
  flex: 0 0 auto;
}

.employee-main-info {
  flex: 1;
}

.employee-name {
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 4px 0;
  transition: color 0.2s ease;
}

.mobile-employee-card.selected .employee-name {
  color: #6c5ce7;
  font-weight: 700;
}

.employee-id {
  font-size: 14px;
  color: #6c757d;
}

.card-body {
  padding: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  text-transform: uppercase;
}

.info-value {
  font-size: 14px;
  color: #212529;
  word-break: break-word;
}

/* 모바일 빈 상태 */
.mobile-empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

/* 모바일 하단 고정 영역 */
.mobile-bottom-area {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.mobile-pagination {
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
  display: flex;
  justify-content: center;
  align-items: center;
  justify-self: center;
}

.mobile-action-buttons {
  padding: 16px;
  display: flex;
  gap: 12px;
}

.mobile-cancel-btn,
.mobile-select-btn {
  flex: 1;
  min-height: 48px !important;
  font-size: 16px !important;
  font-weight: 500 !important;
}

/* 인쇄 시 데스크톱 레이아웃 강제 적용 */
@media print {
  .mobile-search-form,
  .mobile-content,
  .mobile-bottom-area {
    display: none !important;
  }

  .vs-row {
    display: block !important;
  }
}
</style>

<style scoped>
.table-layout {
  border: 1px solid #cdcdcd;
}

.table-layout .trow {
  border-bottom: 1px solid #cdcdcd;
}

.table-layout .tcol {
  border-right: 1px solid #cdcdcd;
}

.vs-con-table.stripe .tr-values.active {
  background: rgb(102, 112, 203) !important;
  color: #fff;
  font-weight: bold;
}

.red {
  color: #ea5455;
}
</style>
